// /home/heliowallet/frontend/services/tokenManager.ts
import { ethers } from 'ethers';
import axios from 'axios';
import { tokenBalanceService } from './tokenBalanceService';
import { priceService } from './priceService';
import type { FailoverProvider } from './rpcManager';
import { config, getApiUrl } from '@/utils/config';

const API_URL = config.apiUrl;

interface Token {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  balance?: string;
  logoURI?: string;
  chainId: number;
  isCustom?: boolean;
  source?: 'api' | 'custom' | 'popular';
  addedAt: number; // Make addedAt required to match chainStore
  hidden?: boolean;
  lastUpdated?: number;
}

interface TokenInfo {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  logoURI?: string;
}

interface BalanceUpdate {
  address: string;
  balance: string;
  success: boolean;
  error?: string;
}

interface TokenManagerConfig {
  maxConcurrentRequests: number;
  batchSize: number;
  retryAttempts: number;
  cacheTimeout: number;
}

const DEFAULT_CONFIG: TokenManagerConfig = {
  maxConcurrentRequests: 3, // Giới hạn cho RPC free
  batchSize: 5, // Nhỏ để tránh timeout
  retryAttempts: 2,
  cacheTimeout: 60 * 1000 // 1 phút
};

class TokenManager {
  private static instance: TokenManager;
  private config: TokenManagerConfig;
  private loadingChains: Set<number> = new Set();
  private tokenInfoCache: Map<string, { info: TokenInfo; timestamp: number }> = new Map();
  
  private constructor(config: Partial<TokenManagerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }
  
  static getInstance(config?: Partial<TokenManagerConfig>): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager(config);
    }
    return TokenManager.instance;
  }
  
  // Lấy danh sách token từ API cho chain cụ thể
  async fetchTokensForChain(chainId: number): Promise<TokenInfo[]> {
    try {
      const response = await axios.get(`${API_URL}/tokens/chain/${chainId}`);
      const tokens: TokenInfo[] = response.data.tokens || [];
      
      // Cache token info
      tokens.forEach(token => {
        const cacheKey = `${chainId}:${token.address.toLowerCase()}`;
        this.tokenInfoCache.set(cacheKey, {
          info: token,
          timestamp: Date.now()
        });
      });
      
      return tokens;
    } catch (error) {
      console.error(`Failed to fetch tokens for chain ${chainId}:`, error);
      return [];
    }
  }
  
  // Lấy popular tokens cho chain
  async fetchPopularTokens(chainId: number, limit: number = 10): Promise<TokenInfo[]> {
    try {
      const response = await axios.get(
        `${API_URL}/tokens/chain/${chainId}?popular=true&limit=${limit}`
      );
      return response.data.tokens || [];
    } catch (error) {
      console.error(`Failed to fetch popular tokens for chain ${chainId}:`, error);
      return [];
    }
  }
  
  // Validate và lấy thông tin token từ contract
  async validateToken(
    provider: ethers.JsonRpcProvider | any,
    tokenAddress: string,
    chainId: number
  ): Promise<TokenInfo | null> {
    // Validate address format first
    if (!tokenAddress || !ethers.isAddress(tokenAddress)) {
      console.error('Invalid token address format:', tokenAddress);
      return null;
    }
    
    // Check cache first
    const cacheKey = `${chainId}:${tokenAddress.toLowerCase()}`;
    const cached = this.tokenInfoCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.config.cacheTimeout) {
      return cached.info;
    }
    
    try {
      // Skip getCode check if provider is from wagmi/viem
      // Just try to interact with the contract directly
      
      const tokenAbi = [
        'function name() view returns (string)',
        'function symbol() view returns (string)',
        'function decimals() view returns (uint8)',
        'function balanceOf(address) view returns (uint256)'
      ];
      
      // Use the address directly without checksum for compatibility
      const tokenContract = new ethers.Contract(tokenAddress, tokenAbi, provider);
      
      // Fetch token info with individual try-catch for each property
      let name = 'Unknown Token';
      let symbol = 'UNKNOWN';
      let decimals = 18;
      let isValidToken = false;
      
      try {
        // Try to get symbol first (most tokens have this)
        const symbolResult = await tokenContract.symbol();
        if (symbolResult) {
          symbol = symbolResult;
          isValidToken = true;
        }
      } catch (error) {
        console.warn('Failed to fetch token symbol:', error);
      }
      
      try {
        // Try to get name
        const nameResult = await tokenContract.name();
        if (nameResult) {
          name = nameResult;
          isValidToken = true;
        }
      } catch (error) {
        console.warn('Failed to fetch token name:', error);
      }
      
      try {
        // Try to get decimals
        const decimalsResult = await tokenContract.decimals();
        if (decimalsResult !== undefined) {
          decimals = Number(decimalsResult);
        }
      } catch (error) {
        console.warn('Failed to fetch token decimals:', error);
      }
      
      // If we couldn't get symbol or name, try balanceOf to verify it's ERC20
      if (!isValidToken) {
        try {
          const testBalance = await tokenContract.balanceOf(ethers.ZeroAddress);
          isValidToken = true;
        } catch (error) {
          console.error('balanceOf check failed:', error);
          return null;
        }
      }
      
      const info: TokenInfo = {
        address: tokenAddress.toLowerCase(),
        name,
        symbol,
        decimals
      };
      
      // Cache result
      this.tokenInfoCache.set(cacheKey, {
        info,
        timestamp: Date.now()
      });
      
      return info;
    } catch (error: any) {
      console.error(`Failed to validate token ${tokenAddress}:`, error);
      console.error('Error details:', error.message, error.code);
      return null;
    }
  }
  
  // Cập nhật balance cho tokens - optimized cho RPC free
  async updateTokenBalances(
    provider: FailoverProvider | ethers.JsonRpcProvider,
    tokens: Token[],
    walletAddress: string,
    rpcs?: string[]
  ): Promise<BalanceUpdate[]> {
    if (tokens.length === 0) return [];
    
    const results: BalanceUpdate[] = [];
    
    // Chia tokens thành batches nhỏ
    const batches: Token[][] = [];
    for (let i = 0; i < tokens.length; i += this.config.batchSize) {
      batches.push(tokens.slice(i, i + this.config.batchSize));
    }
    
    // Process từng batch tuần tự để không overload RPC
    for (const batch of batches) {
      try {
        // Thử với provider chính trước
        const batchResults = await this.fetchBatchBalances(provider, batch, walletAddress);
        results.push(...batchResults);
        
        // Delay giữa các batch
        if (batches.indexOf(batch) < batches.length - 1) {
          await this.delay(200); // 200ms delay
        }
      } catch (error) {
        console.error('Batch failed with primary provider:', error);
        
        // Nếu có RPC pool, thử fallback
        if (rpcs && rpcs.length > 0) {
          try {
            const poolResults = await tokenBalanceService.fetchBalancesWithPool(
              rpcs,
              batch,
              walletAddress
            );
            
            results.push(...poolResults.map(r => ({
              address: r.address,
              balance: r.balance,
              success: r.success,
              error: r.error
            })));
          } catch (poolError) {
            console.error('Pool fallback also failed:', poolError);
            
            // Mark all tokens in batch as failed
            batch.forEach(token => {
              results.push({
                address: token.address,
                balance: '0',
                success: false,
                error: 'Failed to fetch balance'
              });
            });
          }
        }
      }
    }
    
    return results;
  }
  
  // Fetch balance cho một batch tokens
  private async fetchBatchBalances(
    provider: ethers.JsonRpcProvider,
    tokens: Token[],
    walletAddress: string
  ): Promise<BalanceUpdate[]> {
    const tokenAbi = ['function balanceOf(address) view returns (uint256)'];
    
    const promises = tokens.map(async (token): Promise<BalanceUpdate> => {
      try {
        const contract = new ethers.Contract(token.address, tokenAbi, provider);
        
        // Timeout cho mỗi request
        const timeout = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 3000)
        );
        
        const fetchBalance = async () => {
          const balance = await contract.balanceOf(walletAddress);
          return ethers.formatUnits(balance, token.decimals);
        };
        
        const balance = await Promise.race([fetchBalance(), timeout]);
        
        return {
          address: token.address,
          balance: balance as string,
          success: true
        };
      } catch (error: any) {
        return {
          address: token.address,
          balance: '0',
          success: false,
          error: error.message
        };
      }
    });
    
    return Promise.all(promises);
  }
  
  // Thêm custom token
  async addCustomToken(
    provider: ethers.JsonRpcProvider,
    tokenAddress: string,
    walletAddress: string,
    chainId: number
  ): Promise<Token | null> {
    // Validate address
    if (!ethers.isAddress(tokenAddress)) {
      throw new Error('Invalid token address');
    }
    
    // Get token info
    const tokenInfo = await this.validateToken(provider, tokenAddress, chainId);
    
    if (!tokenInfo) {
      throw new Error('Failed to fetch token information. Make sure this is a valid ERC20 token on the correct network.');
    }
    
    // Get balance
    let balance = '0';
    try {
      const balanceResult = await this.fetchBatchBalances(
        provider,
        [{
          ...tokenInfo,
          address: tokenAddress,
          chainId,
          balance: '0',
          addedAt: Date.now() // Add missing addedAt
        }],
        walletAddress
      );
      
      if (balanceResult[0]?.success) {
        balance = balanceResult[0].balance;
      }
    } catch (error) {
      console.warn('Failed to fetch initial balance:', error);
    }
    
    const token = {
      ...tokenInfo,
      balance,
      isCustom: true,
      source: 'custom' as const,
      chainId,
      addedAt: Date.now()
    };
    
    return token;
  }
  
  // Import popular tokens một cách thông minh
  async importPopularTokens(
    provider: ethers.JsonRpcProvider,
    chainId: number,
    walletAddress: string,
    existingTokens: Set<string>
  ): Promise<Token[]> {
    const popularTokens = await this.fetchPopularTokens(chainId);
    
    // Lọc tokens chưa có
    const newTokens = popularTokens.filter(
      token => !existingTokens.has(token.address.toLowerCase())
    );
    
    if (newTokens.length === 0) {
      return [];
    }
    
    const importedTokens: Token[] = [];
    
    // Import từng batch
    const batches: TokenInfo[][] = [];
    for (let i = 0; i < newTokens.length; i += this.config.batchSize) {
      batches.push(newTokens.slice(i, i + this.config.batchSize));
    }
    
    for (const batch of batches) {
      const batchTokens = batch.map(info => ({
        ...info,
        chainId,
        source: 'popular' as const,
        balance: '0',
        addedAt: Date.now() // Add addedAt here
      }));
      
      // Fetch balances
      try {
        const balanceUpdates = await this.fetchBatchBalances(
          provider,
          batchTokens,
          walletAddress
        );
        
        // Merge balance updates
        batchTokens.forEach((token, index) => {
          const update = balanceUpdates[index];
          if (update?.success) {
            token.balance = update.balance;
          }
        });
      } catch (error) {
        console.error('Failed to fetch balances for batch:', error);
      }
      
      importedTokens.push(...batchTokens);
      
      // Delay between batches
      if (batches.indexOf(batch) < batches.length - 1) {
        await this.delay(300);
      }
    }
    
    return importedTokens;
  }
  
  // Lấy token prices
  async fetchTokenPrices(
    chainId: number,
    tokenAddresses: string[]
  ): Promise<Record<string, number>> {
    return priceService.getTokenPrices(chainId, tokenAddresses);
  }
  
  // Helper delay function
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // Check if chain is loading
  isChainLoading(chainId: number): boolean {
    return this.loadingChains.has(chainId);
  }
  
  // Set chain loading state
  setChainLoading(chainId: number, loading: boolean): void {
    if (loading) {
      this.loadingChains.add(chainId);
    } else {
      this.loadingChains.delete(chainId);
    }
  }
  
  // Clear cache for chain
  clearChainCache(chainId: number): void {
    const keysToDelete: string[] = [];
    
    this.tokenInfoCache.forEach((_, key) => {
      if (key.startsWith(`${chainId}:`)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.tokenInfoCache.delete(key));
  }
  
  // Get cache stats
  getCacheStats(): {
    totalCached: number;
    byChain: Record<number, number>;
  } {
    const byChain: Record<number, number> = {};
    
    this.tokenInfoCache.forEach((_, key) => {
      const chainId = parseInt(key.split(':')[0]);
      byChain[chainId] = (byChain[chainId] || 0) + 1;
    });
    
    return {
      totalCached: this.tokenInfoCache.size,
      byChain
    };
  }
}

export const tokenManager = TokenManager.getInstance();

// Export types
export type { Token, TokenInfo, BalanceUpdate, TokenManagerConfig };